// Pure logic for the questionnaire select primitive (JSX-free so tests and
// non-UI callers can import it). The option shape mirrors
// TenantAdminOutboundAnswerNormalizationOptionV1 so one authored list drives
// both voice normalization and the dropdown.

export interface QuestionnaireSelectOption {
  value: string;
  label: string;
  spokenAliases: string[];
}

export interface QuestionnaireSelectValue {
  value: string;
  unlisted: boolean;
}

/**
 * Choice answers exist in two shapes: UI wrappers ({ value, unlisted }) while
 * editing, and canonical plain values once committed/saved — and committed
 * values flow back into the editor (discard-and-reopen restores
 * committedAnswers). Rendering a canonical string through the wrapper shape
 * produced blank selection chips, so hydrate every entry here instead of
 * trusting the stored shape.
 */
export function hydrateQuestionnaireSelectValues(
  raw: unknown,
  options: QuestionnaireSelectOption[],
): QuestionnaireSelectValue[] {
  // single_choice canonicalizes to a BARE STRING, not an array
  // (canonicalizeQuestionnaireUiAnswers: `? values[0] : values`), and
  // buildRecordQuestionnaireAnswers returns raw record values the same way.
  // Treating those as "not an array" rendered 17 of the VIP template's choice
  // questions as unanswered — worse than the blank chip this helper fixes,
  // because "nothing selected" reads as "the patient never answered".
  const entries = typeof raw === "string" ? [raw] : raw;
  if (!Array.isArray(entries)) return [];

  const knownValues = new Set(options.map((option) => option.value));
  const hydrated: QuestionnaireSelectValue[] = [];
  for (const entry of entries) {
    if (typeof entry === "string") {
      if (entry.length > 0) {
        hydrated.push({ unlisted: !knownValues.has(entry), value: entry });
      }
      continue;
    }
    if (entry != null && typeof entry === "object") {
      const value = (entry as { value?: unknown }).value;
      if (typeof value === "string" && value.length > 0) {
        hydrated.push({
          unlisted: (entry as { unlisted?: unknown }).unlisted === true,
          value,
        });
      }
    }
  }
  return hydrated;
}

// Date parsing lives in contracts (shared with the server-side overlay and
// call ingest) — re-exported here so field primitives keep one import site.
export { parseIsoDateTextV1 as parseIsoDateText } from "@amplecare/contracts";
export type { IsoDatePartsV1 as IsoDateParts } from "@amplecare/contracts";

export function filterQuestionnaireSelectOptions(
  query: string,
  options: QuestionnaireSelectOption[],
): QuestionnaireSelectOption[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...options];

  const scored = options
    .map((option) => {
      const label = option.label.toLowerCase();
      let score = 0;
      if (label.startsWith(needle)) score = 3;
      else if (label.includes(needle)) score = 2;
      else if (
        option.spokenAliases.some((alias) =>
          alias.toLowerCase().includes(needle),
        )
      ) {
        score = 1;
      }
      return { option, score };
    })
    .filter((entry) => entry.score > 0);

  scored.sort((a, b) => b.score - a.score);
  return scored.map((entry) => entry.option);
}
